/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
  Box,
  CircularProgress,
  Divider,
} from '@material-ui/core';
import { DateTime } from 'luxon';

function ModalStatusHistory({ isOpen, onClose, statusHistoryData, isLoading, invoiceCreatedAt, invoiceInitialStatus }) {
  const formatDate = dateString => {
    if (!dateString) {
      return '--';
    }
    try {
      return DateTime.fromISO(dateString).toFormat('dd/MM/yyyy HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  const formatTimeElapsed = (startDate, endDate) => {
    if (!startDate || !endDate) {
      return '--';
    }
    try {
      const start = DateTime.fromISO(startDate);
      const end = DateTime.fromISO(endDate);
      const diff = end.diff(start, ['days', 'hours', 'minutes', 'seconds']);

      const parts = [];
      if (diff.days > 0) {
        parts.push(`${Math.floor(diff.days)}d`);
      }
      if (diff.hours > 0) {
        parts.push(`${Math.floor(diff.hours)}h`);
      }
      if (diff.minutes > 0) {
        parts.push(`${Math.floor(diff.minutes)}min`);
      }
      if (diff.seconds > 0 && parts.length === 0) {
        parts.push(`${Math.floor(diff.seconds)}s`);
      }

      return parts.length > 0 ? parts.join(' ') : '< 1s';
    } catch {
      return '--';
    }
  };

  const getStatusLabel = status => {
    if (!status) {
      return '--';
    }

    const statusMap = {
      PENDENTE_ASSINATURA_DE_CONTRATO: 'Pendente assinatura de contrato',
      PENDENTE_CADASTRO_ORACLE: 'Pendente cadastro Oracle',
      AGUARDANDO_VALIDACAO_INTERNA: 'Aguardando validação interna',
      AGUARDANDO_VALIDACAO_PARCEIRO: 'Aguardando validação parceiro',
      PENDENTE_GERACAO_OC: 'Pendente geração de OC',
      PENDENTE_VALIDACAO_ORACLE: 'Pendente validação Oracle',
      AGUARDANDO_ENVIO_NF: 'Aguardando envio de NF',
      PENDENTE_ENVIO_NF_ORACLE: 'Pendente envio de NF para a Oracle',
      FALHA_ENVIO_NF_ORACLE: 'Falha de envio de NF para a Oracle',
      PROCESSO_DE_PAGAMENTO: 'Pagamento em andamento',
      PAGO: 'Pago',
      NF_RECUSADA: 'Nota recusada',
      ESTORNO_BANCARIO: 'Estorno bancário',
      FLUXO_MANUAL: 'Fluxo manual',
    };

    return statusMap[status] || status;
  };

  const getAlteradoPorLabel = tipo => {
    const labels = {
      BACKSTAGE_USER: 'Usuário Backstage',
      CLIENTE: 'Cliente',
      SISTEMA: 'Sistema',
    };
    return labels[tipo] || tipo;
  };

  const allHistoryItems = [];

  if (statusHistoryData?.data && statusHistoryData.data.length > 0) {
    allHistoryItems.push(...statusHistoryData.data);

    const oldestLog = statusHistoryData.data[statusHistoryData.data.length - 1];
    if (invoiceCreatedAt && oldestLog.statusAnterior) {
      allHistoryItems.push({
        id: 'initial',
        statusAnterior: null,
        statusNovo: oldestLog.statusAnterior,
        alteradoPorTipo: 'SISTEMA',
        alteradoPorId: null,
        alteradoPorNome: 'Criação da Invoice',
        dataHoraAlteracao: invoiceCreatedAt,
        isInitial: true,
      });
    }
  } else if (invoiceCreatedAt && invoiceInitialStatus) {
    allHistoryItems.push({
      id: 'initial',
      statusAnterior: null,
      statusNovo: invoiceInitialStatus,
      alteradoPorTipo: 'SISTEMA',
      alteradoPorId: null,
      alteradoPorNome: 'Criação da Invoice',
      dataHoraAlteracao: invoiceCreatedAt,
      isInitial: true,
    });
  }

  const sortedHistory = [...allHistoryItems].sort((a, b) => {
    const dateA = new Date(a.dataHoraAlteracao);
    const dateB = new Date(b.dataHoraAlteracao);
    return dateB - dateA;
  });

  return (
    <Dialog open={isOpen} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography>Histórico de Alterações de Status</Typography>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" style={{ minHeight: '200px' }}>
            <CircularProgress />
          </Box>
        ) : sortedHistory.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" style={{ minHeight: '200px' }}>
            <Typography variant="body2">Nenhum histórico encontrado</Typography>
          </Box>
        ) : (
          <Box style={{ marginTop: '16px' }}>
            {sortedHistory.map((item, index) => {
              const nextItem = index < sortedHistory.length - 1 ? sortedHistory[index + 1] : null;
              const timeElapsed = nextItem
                ? formatTimeElapsed(nextItem.dataHoraAlteracao, item.dataHoraAlteracao)
                : null;

              return (
                <Box key={item.id || `item-${index}`} style={{ marginBottom: '24px' }}>
                  <Box display="flex" alignItems="flex-start" style={{ marginBottom: '8px' }}>
                    <Box
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: item.isInitial ? '#4caf50' : '#2196f3',
                        marginRight: '12px',
                        marginTop: '6px',
                        flexShrink: 0,
                      }}
                    />
                    <Box style={{ flex: 1 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        style={{ marginBottom: '4px' }}
                      >
                        <Typography variant="body1" style={{ fontWeight: 600 }}>
                          {item.statusAnterior ? (
                            <>
                              {getStatusLabel(item.statusAnterior)} → {getStatusLabel(item.statusNovo)}
                            </>
                          ) : (
                            <>Status Inicial: {getStatusLabel(item.statusNovo)}</>
                          )}
                        </Typography>
                        <Typography variant="caption" style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
                          {formatDate(item.dataHoraAlteracao)}
                        </Typography>
                      </Box>

                      <Box style={{ marginTop: '8px' }}>
                        <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                          <strong>Alterado por:</strong> {getAlteradoPorLabel(item.alteradoPorTipo)}
                          {item.alteradoPorNome && ` (${item.alteradoPorNome})`}
                        </Typography>
                      </Box>

                      {timeElapsed && (
                        <Box style={{ marginTop: '4px' }}>
                          <Typography variant="caption" style={{ color: 'rgba(0, 0, 0, 0.54)', fontStyle: 'italic' }}>
                            Tempo decorrido: {timeElapsed}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {index < sortedHistory.length - 1 && (
                    <Box
                      style={{
                        width: '2px',
                        height: '24px',
                        backgroundColor: '#e0e0e0',
                        marginLeft: '5px',
                        marginTop: '4px',
                      }}
                    />
                  )}

                  {index < sortedHistory.length - 1 && <Divider style={{ marginTop: '16px' }} />}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ModalStatusHistory.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  statusHistoryData: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        statusAnterior: PropTypes.string,
        statusNovo: PropTypes.string,
        alteradoPorTipo: PropTypes.string,
        alteradoPorId: PropTypes.string,
        alteradoPorNome: PropTypes.string,
        dataHoraAlteracao: PropTypes.string,
      }),
    ),
    total: PropTypes.number,
    page: PropTypes.number,
    limit: PropTypes.number,
    totalPages: PropTypes.number,
  }),
  isLoading: PropTypes.bool,
  invoiceCreatedAt: PropTypes.string,
  invoiceInitialStatus: PropTypes.string,
};

ModalStatusHistory.defaultProps = {
  statusHistoryData: null,
  isLoading: false,
  invoiceCreatedAt: null,
  invoiceInitialStatus: null,
};

export default ModalStatusHistory;
